<template>
  <div class="tw-rounded tw-border tw-border-solid tw-border-gray-100 tw-overflow-auto">
    <table class="tw-min-w-full tw-leading-normal tw-w-full">
      <thead class="">
        <tr>
          <th v-for="c in visibleColumns" :key="c.name" :class="c.classes"
            class="tw-px-5 tw-text-[10px] last:tw-border-e-0 tw-whitespace-nowrap tw-border-e tw-py-3 tw-border-b tw-borderx tw-border-gray-100 tw-bg-gray-50 tw-text-start tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider">
            <!-- Conditionally render sortable header for id, created_at and updated_at columns -->
            <div v-if="c.name === 'id' || c.name === 'created_at' || c.name === 'updated_at' || c.name === 'reconfirmed_at'" 
                 class="tw-flex tw-items-center tw-gap-1 tw-cursor-pointer"
                 @click="handleSort(c.name)">
              {{ $t(c.label) }}
              <span v-if="props.sortField === c.name" class="tw-text-primary-500">
                <icon :icon="props.sortDirection === 'asc' ? 'mdi:sort-ascending' : 'mdi:sort-descending'" class="tw-w-4 tw-h-4" />
              </span>
              <span v-else class="tw-text-gray-300">
                <icon icon="mdi:sort" class="tw-w-4 tw-h-4" />
              </span>
            </div>
            <!-- Regular header for other columns -->
            <span v-else>{{ $t(c.label) }}</span>
          </th>
        </tr>
      </thead>

      <tbody v-if="props.loading">
        <tr class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50"
          v-for="i in 10" :key="i">
          <td v-for="c in visibleColumns" :key="c.name" class="tw-px-2 tw-py-3">
            <div
              class="tw-w-full tw-py-3 tw-bg-gray-50 group-even:tw-bg-white  tw-rounded tw-min-w-[30px] tw-animate-pulse">

            </div>
          </td>
        </tr>
      </tbody>


      <tbody v-else>
        <template v-if="props.items.length > 0">
          <OrderTableRow 
          @click="onRowClick(item)" 
          v-for="item in props.items" 
          :key="item.id + item.updated_at" 
          :item="item" 
          :visibleColumns="visibleColumnsMap" 
          :columnOrder="columnOrder"
          :class="[selectedRowId == item.id && '!tw-bg-primary-100']"
          />
        </template>
        <template v-else>
          <tr>
            <td :colspan="visibleColumns.length">
              <div
                class="tw-p-5 tw-bg-white tw-min-h-[250px]  tw-text-gray-600 tw-flex tw-items-center tw-justify-center tw-text-lg">
                <p>{{ $t('messages.table-no-results') }}</p>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

  </div>

  <div class="tw-my-4 tw-flex tw-items-center tw-justify-between">
    <div class="tw-flex tw-items-center tw-gap-2">
      <button @click="showColumnSelector = true" class="tw-p-1.5 tw-rounded tw-bg-gray-50 tw-border tw-border-solid tw-border-gray-200 tw-text-gray-500 hover:tw-text-primary-500 hover:tw-border-primary-300 hover:tw-bg-primary-50/50 tw-duration-200 tw-flex tw-items-center tw-justify-center">
        <icon icon="solar:settings-bold-duotone" class="tw-w-5 tw-h-5" />
        <span class="tw-ml-1 tw-text-xs tw-font-medium tw-hidden sm:tw-inline-block">{{ $t('buttons.columns') }}</span>
      </button>
      
      <!-- Current sort indicator -->
      <div v-if="props.sortField" class="tw-text-xs tw-text-gray-500">
        {{ $t('common.sorted_by') }}: {{ $t(`fields.${props.sortField}`) }} ({{ props.sortDirection === 'asc' ? $t('common.ascending') : $t('common.descending') }})
      </div>
    </div>

    <TablePagination v-if="props.items.length > 0" :has-next="!!options.next_page_url"
      :has-previous="!!options.prev_page_url" @previous="emit('previous')" @next="emit('next')" />
  </div>
    
  <ColumnSelector 
    :visible="showColumnSelector" 
    :columns="columns" 
    :storage-key="'admin_orders_columns'"
    @update:visible="onUpdateColumnSelectorVisible"
    @update:columns="updateVisibleColumns" />
</template>

<script setup>
import { computed, defineProps, defineEmits, inject, reactive, onMounted, watch, ref } from 'vue'
import TablePagination from '@/components/table/TablePagination'
import OrderTableRow from './OrderTableRow'
import ColumnSelector from '@/components/table/ColumnSelector'
import { getFromLocalStorage } from '@/utils/local-storage'

const props = defineProps({
  loading: Boolean,
  data: Object,
  items: Array,
  options: Object,
  showColumnSelector: {
    type: Boolean,
    default: false
  },
  // Add props for server-side sorting
  sortField: {
    type: String,
    default: ''
  },
  sortDirection: {
    type: String,
    default: 'asc'
  }
});

const emit = defineEmits([
  'next', 
  'previous', 
  'update:show-column-selector',
  'sort' // Add new emit for sorting
])

const active = inject('active')

const selectedRowId = ref(null);

// Function to handle sort click
const handleSort = (field) => {
  // Determine the new direction
  let newDirection = 'asc';
  if (props.sortField === field) {
    newDirection = props.sortDirection === 'asc' ? 'desc' : 'asc';
  }
  
  // Emit the sort event with the field and direction
  emit('sort', { field, direction: newDirection });
};

const showColumnSelector = computed({
  get: () => props.showColumnSelector,
  set: (value) => emit('update:show-column-selector', value)
});

const visibleColumnsMap = reactive({});
const columnOrder = reactive([]);

const columns = computed(() => {
  let cols = [
    { name: 'id', label: 'fields.id', classes: ['tw-w-[30px]'], sortable: true },
    { name: 'customer_name', label: 'fields.customer_name', classes: ['tw-w-[160px]'] },
    { name: 'customer_phone', label: 'fields.customer_phone', classes: ['tw-w-[120px]'] },
    { name: 'customer_city', label: 'fields.customer_city', classes: ['tw-max-w-[120px]'] },
    { name: 'customer_area', label: 'fields.customer_area', classes: ['tw-max-w-[120px]'] },
    { name: 'customer_address', label: 'fields.customer_address', classes: ['tw-max-w-[150px]'] },
    { name: 'customer_notes', label: 'fields.customer_notes', classes: ['tw-max-w-[150px]'] },
    { name: 'order_summary', label: 'fields.order_summary', classes: ['tw-max-w-[120px]'] },
    { name: 'price', label: 'fields.price', classes: ['tw-w-[140px]'] },
    { name: 'calls', label: 'fields.calls', classes: ['tw-w-[70px]'] },
    (
      active.value.followup ?
        { name: 'followup_status', label: 'fields.followup_status', classes: ['tw-w-[120px]'] }
        :
        { name: 'agent_status', label: 'fields.agent_status', classes: ['tw-w-[120px]'] }
    ),
    { name: 'delivery_id', label: 'fields.delivery_id', classes: ['tw-w-[120px]'] },
    { name: 'delivery_status', label: 'fields.delivery_status', classes: ['tw-w-[120px]'] },
    { name: 'delivery_time', label: 'fields.delivery_time', classes: ['tw-w-[100px]'] },
    { name: 'external_tracking', label: 'fields.external_tracking', classes: ['tw-w-[120px]'] },
    { name: 'agent_notes', label: 'fields.agent_notes', classes: ['tw-w-[150px]'] },
    { name: 'created_at', label: 'fields.created_at', classes: ['tw-w-[120px]'], sortable: true },
    { name: 'updated_at', label: 'fields.updated_at', classes: ['tw-w-[120px]'], sortable: true },
    { name: 'reconfirmed_at', label: 'fields.reconfirmed_at', classes: ['tw-w-[120px]'] },
    { name: 'actions', label: 'fields.actions', classes: ['tw-w-[100px]'] },
  ]

  return cols;
})

const visibleColumns = computed(() => {
  // First, filter by visibility
  const visibleCols = columns.value.filter(column => visibleColumnsMap[column.name] !== false);
  
  // Then, sort by the order
  if (columnOrder.length > 0) {
    return visibleCols.sort((a, b) => {
      const indexA = columnOrder.indexOf(a.name);
      const indexB = columnOrder.indexOf(b.name);
      
      // If both are in the order array, sort by their index
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only a is in the order array, it comes first
      else if (indexA !== -1) {
        return -1;
      }
      // If only b is in the order array, it comes first
      else if (indexB !== -1) {
        return 1;
      }
      // If neither is in the order array, keep original order
      return 0;
    });
  }
  
  return visibleCols;
});

const updateVisibleColumns = (newData) => {
  if (newData.visibility) {
    // Update the reactive visibleColumnsMap with the new values
    Object.keys(newData.visibility).forEach(key => {
      visibleColumnsMap[key] = newData.visibility[key];
    });
  }
  
  if (newData.order) {
    // Update the column order
    columnOrder.length = 0; // Clear the array
    newData.order.forEach(colName => {
      columnOrder.push(colName);
    });
  }
};

const onUpdateColumnSelectorVisible = (value) => {
  showColumnSelector.value = value;
};

// Watch for external changes to showColumnSelector
watch(() => props.showColumnSelector, (newValue) => {
  if (newValue !== showColumnSelector.value) {
    showColumnSelector.value = newValue;
  }
});

// Initialize visible columns from localStorage or default to all visible
onMounted(() => {
  const savedData = getFromLocalStorage('admin_orders_columns', null);
  
  // Default columns that should be visible
  const defaultVisibleColumns = [
    'id', 'customer_name', 'customer_phone', 'customer_city', 
    'order_summary', 'price', 'calls', 'agent_status', 'followup_status', 
    'delivery_id', 'delivery_status', 'delivery_time', 'actions'
  ];
  
  if (savedData) {
    let savedColumns;
    let savedOrder;
    
    if (typeof savedData === 'object' && savedData !== null) {
      if (savedData.columns) {
        savedColumns = savedData.columns;
      }
      if (savedData.order) {
        savedOrder = savedData.order;
      }
    } else {
      // For backward compatibility with older format
      savedColumns = savedData;
    }
    
    // Initialize visibility from saved preferences
    if (savedColumns) {
      columns.value.forEach(column => {
        // If the column exists in saved preferences, use that value, otherwise default to visible for specific columns
        visibleColumnsMap[column.name] = savedColumns[column.name] !== undefined 
          ? savedColumns[column.name] 
          : defaultVisibleColumns.includes(column.name);
      });
    }
    
    // Initialize column order from saved preferences
    if (savedOrder) {
      savedOrder.forEach(colName => {
        columnOrder.push(colName);
      });
    }
  } else {
    // Default: show only default columns
    columns.value.forEach(column => {
      visibleColumnsMap[column.name] = defaultVisibleColumns.includes(column.name);
    });
  }
  
  // Always show actions column
  if (columns.value.some(col => col.name === 'actions')) {
    visibleColumnsMap['actions'] = true;
  }
});

const onRowClick = item => {
  selectedRowId.value = item.id
}
</script>

<style></style>
